import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Text } from "../../ui/components/Text";
import { Button } from "../../ui/components/Button";
import { AuthTransitionScreen } from "../../ui/components/AuthTransitionScreen";
import { useAuth } from "../lib/auth/hooks";
import { useBooleanFeatureFlag } from "../lib/featureFlags/hooks";
import { useAtomValue } from "jotai";
import { featureFlagsLoadedAtom } from "../lib/featureFlags/atoms";
import { getCertificationAppOrigin } from "../lib/auth/app-origins";

function isProfilePath(pathname: string): boolean {
  return pathname === "/profile" || pathname.startsWith("/profile/");
}

/**
 * Soft gate for pro-web: when pro_web.access_enabled resolves false (user ?? org ?? global),
 * show invite-only. Super-admins always pass. Profile + certification are ungated.
 */
export function ProAccessGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const loaded = useAtomValue(featureFlagsLoadedAtom);
  const flagEnabled = useBooleanFeatureFlag("pro_web.access_enabled", false);
  const isSuperAdmin = Boolean(user?.roles?.includes("super-admin"));

  if (!loaded) {
    return <AuthTransitionScreen message="Checking access…" />;
  }

  if (isSuperAdmin || flagEnabled || isProfilePath(pathname)) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        maxWidth: 480,
        margin: "80px auto",
        padding: 32,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Text level="title-small" weight="medium">
        Lion Pro is invite-only
      </Text>
      <Text level="body" color="muted">
        Thanks for signing in. Access to Lion Pro is limited while we roll out
        the platform. If you were invited to an organization or product, ask
        your organization administrator to request access from Lion Energy.
        You can still use the certification portal and your profile.
      </Text>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <Button
          color="primary"
          onClick={() => {
            window.location.href = `${getCertificationAppOrigin()}/`;
          }}
        >
          Open Certification
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            void navigate({ to: "/profile" });
          }}
        >
          Profile
        </Button>
      </div>
    </div>
  );
}
